import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { LanguagesForm } from '@components/templates/resume/forms/LanguagesForm';
import { FooterButtons } from '@components/templates/job/after-apply/FooterButtons';
import { bjToast } from '@components/common';
import {
  ApiLanguageAdd,
  ApiLanguageAddPayload,
  fetcher,
  isLanguageAddOk, isApiV1Errors,
} from '@utils/data-fetching';
import { IDisplayedLanguage } from 'src/types/displayed-languages';

interface IProps {
  onSaveSuccess: () => void,
  locale: Locale,
}

const errorToastContent = (errorMessages: Set<string>) => (
  <>
    {Array.from(errorMessages).map((msg) => (
      <div key={msg}>{msg}</div>
    ))}
  </>
);

export const Languages = ({ onSaveSuccess, locale }: IProps) => {
  const { t } = useTranslation('common');

  const [selectedLanguages, setSelectedLanguages] = useState<IDisplayedLanguage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const onSubmit = () => {
    if (!selectedLanguages.length) {
      bjToast.error(t('onboarding.step5.error.language.selected.level'));
      return;
    }

    setIsLoading(true);

    const promises = selectedLanguages.map(async (selectedLanguage) => fetcher<ApiLanguageAdd>(
      '/v1/resume/spoken-language',
      locale,
      {
        method: 'POST',
        stringifyPayload: true,
        payload: {
          shortName: selectedLanguage.shortName,
          level: selectedLanguage.level,
        },
      },
    ).then((response) => {
      if (isApiV1Errors(response)) {
        selectedLanguage.hasError = true;
      }
      return response;
    }));

    Promise.all(promises)
      .then((responses) => {
        let totalGoodResponses = 0;
        const errorMessages: Set<string> = new Set();

        responses.forEach((response) => {
          if (isLanguageAddOk(response)) {
            totalGoodResponses += 1;
          }
          if (isApiV1Errors(response)) {
            errorMessages.add(response.errors[0].message);
          }
        });
        if (totalGoodResponses === responses.length) {
          onSaveSuccess();
        } else {
          bjToast.error(errorToastContent(errorMessages));
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div>
      <p className="pb-6 text-lg">
        {t('apply.wizard.languages.subtitle')}
      </p>

      <LanguagesForm
        setSelectedLanguages={setSelectedLanguages}
        isEnglishSelected={false}
        locale={locale}
      />

      <FooterButtons
        submitType="button"
        onSubmit={onSubmit}
        isLoading={isLoading}
      />
    </div>
  );
};
